/* Asset cache reset: R148 recovery from pre-release URL caching. */
// Reposts, disappearing messages, time controls and teen supervision.
(function (root, factory) {
  const api = factory();
  if (typeof module === "object" && module.exports) module.exports = api;
  if (root) root.LouderrSocialExtras = api;
})(typeof window === "undefined" ? null : window, function () {
  "use strict";

  const TIMER_CHOICES = Object.freeze([
    [null, "Off"], [86400, "24 hours"], [604800, "7 days"], [7776000, "90 days"],
  ]);
  const DAILY_LIMITS = Object.freeze([null, 15, 30, 45, 60, 90, 120, 180]);
  const BREAK_CHOICES = Object.freeze([null, 10, 20, 30, 60]);
  const HEARTBEAT_SECONDS = 60;

  function minutesLabel(minutes) {
    if (minutes === null || minutes === undefined) return "Off";
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    return rest ? `${hours} h ${rest} min` : `${hours} h`;
  }

  function expiryLabel(iso, now = Date.now()) {
    const at = Date.parse(iso || "");
    if (!Number.isFinite(at)) return "";
    const minutes = Math.max(0, Math.round((at - now) / 60000));
    if (minutes < 1) return "Disappearing now";
    if (minutes < 60) return `Disappears in ${minutes} min`;
    const hours = Math.round(minutes / 60);
    if (hours < 48) return `Disappears in ${hours} h`;
    return `Disappears in ${Math.round(hours / 24)} days`;
  }

  function localDay(timeZone, now = new Date()) {
    try {
      const parts = new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" })
        .formatToParts(now);
      const value = type => parts.find(part => part.type === type)?.value;
      return `${value("year")}-${value("month")}-${value("day")}`;
    } catch (_) {
      return now.toISOString().slice(0, 10);
    }
  }

  function timeToMinutes(value) {
    const match = /^(\d{2}):(\d{2})$/.exec(value || "");
    if (!match) return null;
    const minutes = Number(match[1]) * 60 + Number(match[2]);
    return minutes >= 0 && minutes <= 1439 ? minutes : null;
  }

  function minutesToTime(value) {
    const minutes = Number.isInteger(value) ? value : 0;
    return `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
  }

  function reminderKey(kind, userId, day) {
    return `louderr.wellbeing.${kind}.${userId}.${day}`;
  }

  function create(hooks) {
    const doc = hooks.document || document;
    const storage = hooks.storage || (() => { try { return window.localStorage; } catch (_) { return null; } })();
    const session = hooks.session || (() => { try { return window.sessionStorage; } catch (_) { return null; } })();
    const state = {
      wellbeing: null, wellbeingFor: null, heartbeat: 0, activeSeconds: 0, breakShown: false,
      repostTab: null, repostWrites: new Map(), repostIntents: new Map(), repostIdentity: null, repostReturn: null,
    };
    const node = (tag, className = "", text = "") => {
      const element = doc.createElement(tag);
      if (className) element.className = className;
      if (text !== "") element.textContent = text;
      return element;
    };
    const viewerId = () => hooks.viewer()?.id || null;
    const say = (message, error = false) => hooks.toast?.(message, error);

    // A modal <dialog>, so it stacks above Settings and the composer, which are modal too.
    function sheet(title) {
      const shell = node("dialog", "sv-overlay sv-sheet-overlay");
      shell.setAttribute("aria-label", title);
      const panel = node("section", "sv-sheet");
      const header = node("header", "sv-sheet-header");
      const close = node("button", "sv-icon-button");
      close.type = "button";
      close.setAttribute("aria-label", "Close");
      close.innerHTML = '<svg aria-hidden="true"><use href="#icon-x"></use></svg>';
      header.append(node("h2", "", title), close);
      const body = node("div", "sv-sheet-body");
      panel.append(header, body);
      shell.append(panel);
      const previous = doc.activeElement;
      function dismiss() {
        if (shell.open && typeof shell.close === "function") shell.close();
        shell.remove();
        if (previous?.isConnected && typeof previous.focus === "function") previous.focus();
      }
      close.addEventListener("click", dismiss);
      shell.addEventListener("click", event => { if (event.target === shell) dismiss(); });
      shell.addEventListener("cancel", event => { event.preventDefault(); dismiss(); });
      doc.body.append(shell);
      if (typeof shell.showModal === "function") shell.showModal();
      else shell.setAttribute("open", "");
      requestAnimationFrame(() => close.focus());
      return { shell, body, dismiss };
    }

    function choiceButton(label, pressed, handler, className = "sv-choice") {
      const element = node("button", className, label);
      element.type = "button";
      element.setAttribute("aria-pressed", String(Boolean(pressed)));
      element.addEventListener("click", handler);
      return element;
    }

    // Reposts ------------------------------------------------------------------------------

    function repostOwner(control = false) {
      const readOwner = control ? hooks.repostControlOwner : hooks.repostOwner;
      const owner = readOwner?.() || [viewerId()];
      return () => {
        const next = readOwner?.() || [viewerId()];
        return owner.length === next.length && owner.every((value, index) => value === next[index]);
      };
    }

    function refreshRepostButton(button, drop) {
      if (!button) return;
      button.repostDrop = drop;
      const active = drop.viewer?.reposted === true;
      button.classList.toggle("is-reposted", active);
      button.classList.toggle("capability-withheld", hooks.capabilities?.().community_writes === false);
      button.setAttribute("aria-pressed", String(active));
      button.setAttribute("aria-label", active ? "Remove repost" : "Repost");
      button.title = active ? "Remove your repost" : "Repost to your profile";
      button.disabled = repostPending(drop.id);
      button.setAttribute("aria-busy", String(button.disabled));
      const label = button.querySelector?.("[data-repost-label]");
      if (label) label.textContent = active ? "Reposted" : "Repost";
    }

    function makeRepostButton(drop, { flow = false } = {}) {
      if (!drop?.id || drop.structured_type === "sponsor" || drop.author_id === viewerId()
          || drop.author?.id === viewerId() || (drop.status !== undefined && drop.status !== "active")) return null;
      const button = node("button", flow ? "flow-repost" : "action-button repost-action");
      button.type = "button";
      button.dataset.repost = drop.id;
      button.dataset.requiresCapability = "community_writes";
      button.innerHTML = '<svg aria-hidden="true"><use href="#icon-repost"></use></svg>';
      if (flow) {
        const label = node("span", "", "Repost"); label.dataset.repostLabel = "true"; button.append(label);
      }
      // Retained cards survive navigation and CSRF renewal. A fresh click gets
      // a new operation owner; only account/session changes retire the control.
      const current = repostOwner(true);
      button.repostCurrent = current;
      button.addEventListener("click", () => {
        if (!current() || button.isConnected === false) return;
        if (!viewerId() || !hooks.canWrite?.()) {
          if (hooks.signIn?.() === true) say("Reposting is currently unavailable.", true);
          return;
        }
        void toggleRepost(button.repostDrop);
      });
      refreshRepostButton(button, drop);
      return button;
    }

    function publishRepostState(dropId, value) {
      hooks.onRepostState?.(dropId, value);
      for (const button of doc.querySelectorAll?.("[data-repost]") || []) {
        if (button.dataset.repost !== dropId || !button.repostCurrent?.()) continue;
        const drop = button.repostDrop;
        if (typeof value.reposted === "boolean") drop.viewer = { ...drop.viewer, reposted: value.reposted };
        refreshRepostButton(button, drop);
      }
    }

    function canRepost(drop) {
      const viewer = hooks.viewer();
      return Boolean(viewer && hooks.canWrite?.() && drop && drop.author_id !== viewer.id
        && drop.author?.id !== viewer.id && drop.structured_type !== "sponsor"
        && (drop.status === undefined || drop.status === "active"));
    }

    async function toggleRepost(drop) {
      if (!canRepost(drop)) return;
      if (repostPending(drop.id)) return;
      const record = { current: repostOwner() };
      const validate = value => {
        if (value?.drop_id !== drop.id || typeof value.reposted !== "boolean" || typeof value.can_repost !== "boolean")
          throw new Error("Repost status could not be confirmed. Try again.");
        return value;
      };
      state.repostWrites.set(drop.id, record);
      publishRepostState(drop.id, { busy: true });
      try {
        const current = validate(await hooks.api(`/api/drops/${encodeURIComponent(drop.id)}/repost`));
        if (!record.current() || !canRepost(drop)) return;
        drop.viewer = { ...drop.viewer, reposted: current.reposted };
        publishRepostState(drop.id, { reposted: current.reposted });
        let result;
        if (current.reposted) {
          if (!(await hooks.confirm?.("Remove your repost?", { body: "It disappears from your profile. The original stays.", confirm: "Remove" }))) return;
          if (!record.current() || !canRepost(drop)) return;
          result = await hooks.api(`/api/drops/${encodeURIComponent(drop.id)}/repost`, { method: "DELETE", csrf: true });
        } else {
          if (!current.can_repost) throw new Error("This Drop cannot be reposted.");
          result = await hooks.api(`/api/drops/${encodeURIComponent(drop.id)}/repost`, { method: "PUT", csrf: true });
        }
        if (!record.current()) return;
        validate(result);
        if (result.reposted === current.reposted) throw new Error("Repost change could not be confirmed. Try again to check its status.");
        drop.viewer = { ...drop.viewer, reposted: result.reposted };
        publishRepostState(drop.id, { reposted: result.reposted });
        say(result.reposted ? "Reposted to your profile" : "Repost removed");
        if (state.repostTab?.userId === viewerId()) void loadReposts(true);
      } catch (error) {
        if (record.current()) say(error?.message || "Repost result could not be confirmed. Try again to check its status.", true);
      } finally {
        if (state.repostWrites.get(drop.id) === record) state.repostWrites.delete(drop.id);
        // Release retained controls even when this operation became stale.
        // refreshRepostButton derives busy state from the current pending owner.
        publishRepostState(drop.id, { busy: false });
      }
    }

    function repostPending(dropId) {
      return Boolean(state.repostWrites.get(dropId)?.current());
    }

    function repostIdentity() {
      return [viewerId(), ...(hooks.repostControlOwner?.() || [])];
    }

    function sameRepostIdentity() {
      const identity = repostIdentity();
      return state.repostIdentity?.length === identity.length
        && state.repostIdentity.every((value, index) => value === identity[index]);
    }

    function syncRepostIdentity() {
      if (sameRepostIdentity()) return;
      clearProfileReposts();
      state.repostIntents.clear(); state.repostReturn = null;
      state.repostIdentity = repostIdentity();
    }

    function currentRepostTab(tab) {
      return Boolean(tab && state.repostTab === tab && sameRepostIdentity() && tab.viewerId === viewerId()
        && (!tab.grids.current || tab.grids.current()));
    }

    function profileRepost(dropId) {
      syncRepostIdentity();
      const tab = state.repostTab;
      return currentRepostTab(tab) && tab.selected === "reposts" ? tab.drops.get(dropId) || null : null;
    }

    function rememberRepostScroll(tab) {
      if (!tab || !sameRepostIdentity() || tab.selected !== "reposts" || !tab.pages) return;
      const position = hooks.repostScroll?.();
      if (position && [position.left, position.top].every(value => Number.isFinite(value) && value >= 0)) {
        tab.intent.scroll = { left: position.left, top: position.top };
      }
    }

    function cancelRepostRead(tab, hideMore = true) {
      if (!tab) return;
      tab.request += 1;
      tab.read?.controller.abort();
      tab.read = null;
      tab.list?.removeAttribute("aria-busy");
      if (tab.more) {
        tab.more.disabled = false; tab.more.setAttribute("aria-disabled", "false"); tab.more.setAttribute("aria-busy", "false");
        if (hideMore) tab.more.hidden = true; tab.more.onclick = null;
      }
    }

    function clearRepostRows(tab) {
      for (const media of tab.list?.querySelectorAll?.("video, audio") || []) media.pause();
      tab.list?.replaceChildren();
      tab.drops.clear(); tab.cursor = null; tab.pages = 0; tab.pageCursors = [];
    }

    function clearProfileReposts() {
      const tab = state.repostTab;
      if (!tab) return;
      cancelRepostRead(tab); tab.discovery?.abort(); clearRepostRows(tab);
      tab.container.replaceChildren(); tab.container.hidden = true;
      if (tab.list) tab.list.hidden = true;
      for (const element of tab.grids.posts || []) element?.classList.remove("sv-tab-hidden");
      state.repostTab = null;
    }

    function leaveProfileReposts({ returnScroll = null } = {}) {
      syncRepostIdentity();
      rememberRepostScroll(state.repostTab);
      clearProfileReposts();
      state.repostReturn = returnScroll && [returnScroll.left, returnScroll.top].every(value => Number.isFinite(value) && value >= 0)
        ? { position: { left: returnScroll.left, top: returnScroll.top }, current: repostOwner() } : null;
    }

    function resetProfileReposts() {
      clearProfileReposts(); state.repostIntents.clear(); state.repostReturn = null; state.repostIdentity = repostIdentity();
    }

    function repostIntent(userId) {
      let intent = state.repostIntents.get(userId);
      if (!intent) intent = { selected: "posts", pages: 0, scroll: null };
      state.repostIntents.delete(userId); state.repostIntents.set(userId, intent);
      while (state.repostIntents.size > 8) state.repostIntents.delete(state.repostIntents.keys().next().value);
      return intent;
    }

    function selectProfileTab(tab, selected) {
      if (!currentRepostTab(tab)) return;
      rememberRepostScroll(tab);
      cancelRepostRead(tab); tab.discovery?.abort(); tab.discovery = null;
      tab.selected = selected; tab.intent.selected = selected;
      for (const [name, button] of Object.entries(tab.buttons)) {
        button.setAttribute("aria-selected", String(name === selected)); button.tabIndex = name === selected ? 0 : -1;
      }
      for (const element of tab.grids.posts || []) element?.classList.toggle("sv-tab-hidden", selected !== "posts");
      if (tab.list) tab.list.hidden = selected !== "reposts";
      if (selected === "reposts") {
        tab.container.hidden = false;
        return loadReposts(true);
      }
      clearRepostRows(tab);
    }

    function renderProfileTabs(container, user, grids) {
      if (!container) return;
      syncRepostIdentity();
      const previous = state.repostTab;
      const retain = user && previous?.userId === user.id && previous.container === container
        && previous.list === grids.repostList && currentRepostTab(previous);
      if (retain) {
        previous.grids = grids; previous.more = grids.repostMore;
        // The final Posts paint must not abort the selected tab's identity-phase read.
        if (grids.revalidate === false) return profileRepostsReady(user.id);
        selectProfileTab(previous, previous.selected);
      } else {
        rememberRepostScroll(previous); clearProfileReposts();
        container.replaceChildren(); container.hidden = true;
        for (const element of grids.posts || []) element?.classList.remove("sv-tab-hidden");
        grids.repostList?.replaceChildren();
        if (grids.repostList) grids.repostList.hidden = true;
        if (grids.repostMore) grids.repostMore.hidden = true;
        if (!user) return;
        const tab = { userId: user.id, viewerId: viewerId(), container, grids, intent: repostIntent(user.id),
          list: grids.repostList, more: grids.repostMore, cursor: null, pages: 0, pageCursors: [],
          request: 0, buttons: {}, drops: new Map(), outcome: undefined };
        if (tab.intent.selected === "reposts" && state.repostReturn?.current()) tab.intent.scroll = state.repostReturn.position;
        state.repostReturn = null;
        state.repostTab = tab;
        container.setAttribute("role", "tablist"); container.setAttribute("aria-label", "Profile content");
        for (const [name, label] of [["posts", "Posts"], ["reposts", "Reposts"]]) {
          const button = choiceButton(label, false, () => {
            if (tab.selected !== name) selectProfileTab(tab, name);
          }, "sv-tab");
          button.setAttribute("role", "tab"); button.removeAttribute("aria-pressed");
          button.addEventListener("keydown", event => {
            if (!currentRepostTab(tab)) return;
            const next = event.key === "Home" ? "posts" : event.key === "End" ? "reposts"
              : ["ArrowLeft", "ArrowRight"].includes(event.key) ? (name === "posts" ? "reposts" : "posts") : null;
            if (!next) return;
            event.preventDefault();
            if (tab.selected !== next) selectProfileTab(tab, next);
            tab.buttons[next].focus();
          });
          tab.buttons[name] = button; container.append(button);
        }
        selectProfileTab(tab, tab.intent.selected);
      }
      const tab = state.repostTab;
      if (tab.selected === "reposts") return profileRepostsReady(tab.userId);
      tab.discovery?.abort();
      const discovery = new AbortController(); tab.discovery = discovery;
      hooks.api(`/api/reposts/by-profile/${encodeURIComponent(user.id)}?limit=1`, { signal: discovery.signal })
        .then(result => {
          if (currentRepostTab(tab) && tab.discovery === discovery && tab.selected === "posts") {
            container.hidden = !(result?.items || []).length;
          }
        }).catch(() => { /* Optional discovery does not interrupt Posts. */ });
    }

    function profileRepostsReady(userId) {
      const tab = state.repostTab;
      if (!currentRepostTab(tab) || tab.userId !== userId || tab.selected !== "reposts") return;
      return tab.read?.promise || tab.outcome;
    }

    async function readRepostPage(url, signal, current) {
      for (let attempt = 0; attempt < 2; attempt++) {
        if (!current()) throw Object.assign(new Error("Reposts cancelled"), { name: "AbortError" });
        try { return await hooks.api(url, { signal }); }
        catch (error) {
          if (!current() || signal.aborted || error.status !== 429 || attempt) throw error;
          const header = String(error.retryAfter ?? "").trim();
          const delay = /^\d+(?:\.\d+)?$/.test(header) ? Number(header) * 1000 : Date.parse(header) - Date.now();
          if (!header || !Number.isFinite(delay) || delay < 0 || delay > 120000) throw error;
          await new Promise((resolve, reject) => {
            let timer;
            const abort = () => {
              clearTimeout(timer); signal.removeEventListener("abort", abort);
              reject(Object.assign(new Error("Reposts cancelled"), { name: "AbortError" }));
            };
            if (signal.aborted) { abort(); return; }
            signal.addEventListener("abort", abort, { once: true });
            timer = setTimeout(() => { signal.removeEventListener("abort", abort); resolve(); }, delay);
          });
        }
      }
    }

    function loadReposts(reset = false) {
      syncRepostIdentity();
      const tab = state.repostTab;
      if (!currentRepostTab(tab) || !tab.list || tab.selected !== "reposts"
        || (!reset && (tab.read || !tab.cursor))) return;
      rememberRepostScroll(tab);
      cancelRepostRead(tab, reset);
      const op = { request: tab.request, controller: new AbortController(), reset,
        depth: reset ? Math.max(1, tab.intent.pages) : 1, cursor: reset ? null : tab.cursor };
      tab.read = op; tab.outcome = undefined;
      op.promise = runRepostRead(tab, op);
      if (reset && tab.intent.scroll && hooks.restoreRepostScroll) {
        // Existing route restoration treats a rejected operation as cancelled. A
        // superseded or failed replay must not restore over its successor's view.
        const rendered = op.promise.then(value => {
          if (value !== true) throw Object.assign(new Error("Repost restoration cancelled"), { name: "AbortError" });
          return value;
        });
        hooks.restoreRepostScroll(rendered, { ...tab.intent.scroll });
      }
      return op.promise;
    }

    async function runRepostRead(tab, op) {
      const current = () => currentRepostTab(tab) && tab.selected === "reposts"
        && tab.read === op && tab.request === op.request && !op.controller.signal.aborted;
      if (op.reset) clearRepostRows(tab);
      tab.status?.remove();
      const status = node("div", "sv-repost-status"); status.setAttribute("role", "status");
      status.append(node("p", "sv-hint", op.reset ? "Loading reposts…" : "Loading earlier reposts…"));
      tab.status = status; tab.list.append(status); tab.list.setAttribute("aria-busy", "true");
      if (tab.more) {
        tab.more.disabled = false; tab.more.setAttribute("aria-disabled", "true"); tab.more.setAttribute("aria-busy", "true");
        tab.more.hidden = op.reset;
      }
      const changed = () => Object.assign(new Error("Reposts changed. Refresh the list to continue."), { repostCursorChanged: true });
      try {
        const pageCursors = op.reset ? [] : [...tab.pageCursors], cursors = new Set(pageCursors);
        const accepted = new Map(tab.drops), incoming = new Map();
        let cursor = op.cursor, pages = 0, next;
        do {
          const query = cursor ? `?before=${encodeURIComponent(cursor)}` : "";
          const result = await readRepostPage(`/api/reposts/by-profile/${encodeURIComponent(tab.userId)}${query}`, op.controller.signal, current);
          if (!current()) return;
          if (!Array.isArray(result?.items) || (result.next_before != null && typeof result.next_before !== "string")) throw new Error("Reposts could not load.");
          if (cursors.has(cursor)) throw changed();
          cursors.add(cursor); pageCursors.push(cursor); pages++;
          let added = 0;
          for (const drop of result.items) {
            if (!drop?.id) throw new Error("Reposts could not load.");
            if (accepted.has(drop.id)) continue;
            accepted.set(drop.id, drop); incoming.set(drop.id, drop); added++;
          }
          next = result.next_before || null;
          if ((next && (cursors.has(next) || !added)) || (result.items.length && !added)) throw changed();
          cursor = next;
        } while (next && pages < op.depth);
        const cards = [];
        for (const drop of incoming.values()) {
          const card = hooks.makeDropCard(drop), wrapper = node("div", "sv-repost");
          wrapper.append(node("p", "sv-repost-note", `Reposted by ${drop.reposted_by?.display_name || drop.reposted_by?.handle || "this profile"}`), card);
          cards.push(wrapper);
        }
        if (!current()) return;
        status.remove(); tab.list.append(...cards); tab.drops = accepted;
        if (!accepted.size) tab.list.append(node("p", "sv-empty", "No reposts yet."));
        tab.cursor = next; tab.pageCursors = pageCursors;
        tab.pages = op.reset ? pages : tab.pages + pages; tab.intent.pages = tab.pages;
        tab.outcome = true;
        if (tab.more) {
          const restoreFocus = !next && doc.activeElement === tab.more;
          tab.more.hidden = !next;
          tab.more.onclick = () => { if (currentRepostTab(tab) && tab.request === op.request) void loadReposts(false); };
          if (restoreFocus) {
            const target = cards[0] || tab.buttons.reposts;
            if (cards[0]) target.setAttribute("tabindex", "-1");
            target.focus({ preventScroll: true });
          }
        }
        hooks.afterRender?.(); return true;
      } catch (error) {
        if (!current()) return;
        tab.outcome = false;
        const refresh = error.repostCursorChanged || (error.status === 409 && error.code === "repost_page_changed")
          || (error.status === 422 && Boolean(op.cursor));
        const headRetry = Boolean(refresh || op.reset || [401, 403, 404].includes(error.status));
        if (refresh || [401, 403, 404].includes(error.status)) clearRepostRows(tab);
        status.setAttribute("role", "alert");
        const retry = node("button", "secondary-button", refresh ? "Refresh reposts" : "Try again"); retry.type = "button";
        retry.addEventListener("click", () => {
          if (currentRepostTab(tab) && tab.request === op.request) void loadReposts(headRetry);
        });
        status.replaceChildren(node("p", "form-error", error?.message || "Reposts could not load."), retry);
        tab.list.append(status);
        if (tab.more) {
          const restoreFocus = doc.activeElement === tab.more; tab.more.hidden = true;
          if (restoreFocus) retry.focus();
        }
        return false;
      } finally {
        if (tab.read === op && tab.request === op.request) {
          tab.read = null; tab.list.removeAttribute("aria-busy");
          if (tab.more) {
            tab.more.disabled = false; tab.more.setAttribute("aria-disabled", "false"); tab.more.setAttribute("aria-busy", "false");
          }
          if (state.repostTab === tab && sameRepostIdentity() && tab.grids.active?.()
              && tab.grids.current && !tab.grids.current()) {
            // A token can retire a read without a replacement repaint. Keep a
            // usable action which rebuilds the current profile's owner closure.
            clearRepostRows(tab); tab.outcome = false;
            const retry = node("button", "secondary-button", "Try again"); retry.type = "button";
            retry.addEventListener("click", () => {
              if (state.repostTab === tab && sameRepostIdentity() && tab.grids.active?.()) tab.grids.refresh?.();
            });
            status.setAttribute("role", "alert");
            status.replaceChildren(node("p", "form-error", "Reposts need a refresh."), retry);
            tab.list.append(status); if (tab.more) tab.more.hidden = true;
          }
        }
      }
    }
    // Disappearing messages ------------------------------------------------------------------

    function openDisappearing(conversation) {
      if (!conversation?.id) return;
      const current = conversation.disappearing?.seconds ?? null;
      const panel = sheet("Disappearing messages");
      panel.body.append(node("p", "sv-hint",
        "New messages disappear for everyone after the time you choose. Messages already sent stay as they are. "
        + "Anyone in the chat can still copy or screenshot a message, and a reported message is kept for review."));
      if (conversation.kind === "group" && !conversation.can_manage) {
        panel.body.append(node("p", "sv-hint", "Only group admins can change this."));
      }
      for (const [seconds, label] of TIMER_CHOICES) {
        const option = choiceButton(label, current === seconds, async () => {
          try {
            const result = await hooks.api(`/api/messages/conversations/${encodeURIComponent(conversation.id)}/disappearing`, {
              method: "PUT", csrf: true, json: { seconds },
            });
            conversation.disappearing = result.disappearing;
            say(seconds ? `New messages disappear after ${label}` : "Disappearing messages are off");
            panel.dismiss();
            hooks.onConversationChanged?.(conversation.id);
          } catch (error) {
            say(error?.message || "The setting did not change.", true);
          }
        });
        option.setAttribute("role", "radio");
        option.setAttribute("aria-checked", String(current === seconds));
        option.disabled = conversation.kind === "group" && !conversation.can_manage;
        panel.body.append(option);
      }
    }

    function renderDisappearingBanner(container, conversation) {
      if (!container) return;
      const timer = conversation?.disappearing;
      container.hidden = !timer;
      container.replaceChildren();
      if (!timer) return;
      const who = timer.changed_by?.display_name || timer.changed_by?.handle;
      const text = node("span", "", `Disappearing messages: ${timer.label}${who ? `. Turned on by ${who}.` : "."}`);
      const change = node("button", "text-button", "Change");
      change.type = "button";
      change.addEventListener("click", () => openDisappearing(conversation));
      container.append(text, change);
    }

    function syncThreadBanner(list, conversation) {
      if (!list?.parentElement) return;
      let banner = list.previousElementSibling?.classList?.contains("sv-disappearing-banner")
        ? list.previousElementSibling : null;
      if (!banner) {
        banner = node("p", "sv-disappearing-banner");
        list.parentElement.insertBefore(banner, list);
      }
      renderDisappearingBanner(banner, conversation);
    }

    // A reply to a Story shows which Story it answered, while the reader can still see it,
    // and a message on a timer says when it goes.
    function decorateMessage(content, message) {
      if (!content || !message || message.deleted) return;
      const reply = message.story_reply;
      if (reply) {
        const card = node("div", "sv-story-reply");
        const mine = message.sender?.id === viewerId();
        const label = reply.available
          ? (reply.owner_is_viewer ? "Replied to your story" : mine ? "You replied to their story" : "Replied to a story")
          : "Replied to a story that is no longer available";
        if (reply.available && reply.thumbnail_url) {
          const open = node("button", "sv-story-reply-thumb");
          open.type = "button";
          open.setAttribute("aria-label", "Open the story");
          const image = node("img");
          image.src = reply.thumbnail_url;
          image.alt = "";
          image.loading = "lazy";
          open.append(image);
          open.addEventListener("click", () => hooks.openStory?.(reply.story_item_id));
          card.append(open);
        }
        card.append(node("small", "", label));
        content.prepend(card);
      }
      if (message.expires_at) {
        const expiry = node("small", "sv-expiry-label", expiryLabel(message.expires_at));
        content.append(expiry);
      }
    }

    // Time controls -----------------------------------------------------------------------------

    // Minutes are counted, and the device time zone kept, only while a time control is on or a
    // guardian supervises the account (the privacy page says so).
    const countsTime = settings => Boolean(settings && (settings.daily_limit_minutes != null
      || settings.break_reminder_minutes != null || settings.quiet_hours || settings.managed_by_guardian));

    async function refreshWellbeing() {
      const id = viewerId();
      if (!id) { state.wellbeing = null; state.wellbeingFor = null; return null; }
      try {
        const result = await hooks.api("/api/account/wellbeing");
        if (viewerId() !== id) return null;
        state.wellbeing = result;
        state.wellbeingFor = id;
        const deviceZone = hooks.timeZone?.();
        if (deviceZone && result.time_zone === "UTC" && deviceZone !== "UTC" && countsTime(result)) {
          try {
            state.wellbeing = await hooks.api("/api/account/wellbeing", { method: "PATCH", csrf: true, json: { time_zone: deviceZone } });
          } catch (_) { /* The server keeps UTC; limits still work. */ }
        }
        return state.wellbeing;
      } catch (_) {
        return null;
      }
    }

    function startTracking() {
      stopTracking();
      state.activeSeconds = 0;
      state.breakShown = false;
      state.heartbeat = setInterval(() => void beat(), HEARTBEAT_SECONDS * 1000);
    }

    function stopTracking() {
      if (state.heartbeat) clearInterval(state.heartbeat);
      state.heartbeat = 0;
    }

    async function beat() {
      const id = viewerId();
      if (!id || doc.visibilityState === "hidden" || !doc.hasFocus?.()) return;
      if (state.wellbeingFor !== id && !(await refreshWellbeing())) return;
      const settings = state.wellbeing;
      if (!countsTime(settings)) return;
      state.activeSeconds += HEARTBEAT_SECONDS;
      try {
        const result = await hooks.api("/api/account/wellbeing/usage", {
          method: "POST", csrf: true,
          json: { seconds: HEARTBEAT_SECONDS, local_day: localDay(settings.time_zone) },
        });
        settings.today_minutes = result.today_minutes;
      } catch (_) { return; }
      checkReminders();
    }

    function remembered(kind) {
      const day = localDay(state.wellbeing?.time_zone || "UTC");
      try { return storage?.getItem(reminderKey(kind, viewerId(), day)); } catch (_) { return null; }
    }

    function remember(kind, value) {
      const day = localDay(state.wellbeing?.time_zone || "UTC");
      try { storage?.setItem(reminderKey(kind, viewerId(), day), value); } catch (_) { /* optional */ }
    }

    function checkReminders(now = Date.now()) {
      const settings = state.wellbeing;
      if (!settings) return;
      const limit = settings.daily_limit_minutes;
      const snoozedUntil = Number(remembered("limit") || 0);
      if (limit && settings.today_minutes >= limit && now >= snoozedUntil) {
        remember("limit", String(now + 15 * 60000));
        showLimitReminder(settings);
        return;
      }
      const breakAfter = settings.break_reminder_minutes;
      if (breakAfter && !state.breakShown && state.activeSeconds >= breakAfter * 60) {
        state.breakShown = true;
        showBreakReminder(breakAfter);
      }
    }

    function showLimitReminder(settings) {
      const panel = sheet("Daily limit reached");
      panel.body.append(node("p", "", settings.managed_by_guardian
        ? `You have used LOUDERR for ${minutesLabel(settings.today_minutes)} today. Your guardian set a limit of ${minutesLabel(settings.daily_limit_minutes)}.`
        : `You have used LOUDERR for ${minutesLabel(settings.today_minutes)} today, your limit is ${minutesLabel(settings.daily_limit_minutes)}.`));
      const done = node("button", "publish-button", "Done for today");
      done.type = "button";
      done.addEventListener("click", () => { remember("limit", String(Date.now() + 24 * 3600000)); panel.dismiss(); });
      const later = node("button", "secondary-button", "Remind me in 15 minutes");
      later.type = "button";
      later.addEventListener("click", () => panel.dismiss());
      panel.body.append(done, later);
    }

    function showBreakReminder(minutes) {
      const panel = sheet("Time for a break?");
      panel.body.append(
        node("p", "", `You have been here for ${minutesLabel(minutes)}. Look away from the screen, stretch, or get a glass of water.`),
      );
      const done = node("button", "publish-button", "OK");
      done.type = "button";
      done.addEventListener("click", () => { state.activeSeconds = 0; state.breakShown = false; panel.dismiss(); });
      panel.body.append(done);
    }

    function usageChart(week) {
      const chart = node("div", "sv-usage-chart");
      chart.setAttribute("role", "img");
      const peak = Math.max(30, ...week.map(day => day.minutes));
      const labels = [];
      for (const day of week) {
        const column = node("div", "sv-usage-day");
        const bar = node("span", "sv-usage-bar");
        bar.style.height = `${Math.round((day.minutes / peak) * 100)}%`;
        const name = new Date(`${day.day}T12:00:00`).toLocaleDateString(undefined, { weekday: "short" });
        column.append(bar, node("small", "", name));
        chart.append(column);
        labels.push(`${name} ${minutesLabel(day.minutes)}`);
      }
      chart.setAttribute("aria-label", `Minutes per day: ${labels.join(", ")}`);
      return chart;
    }

    function limitControls(values, disabled, onSave) {
      const wrapper = node("div", "sv-form");
      const limitLabel = node("label", "", "Daily limit");
      const limit = node("select");
      for (const value of DAILY_LIMITS) {
        const option = node("option", "", minutesLabel(value));
        option.value = value === null ? "" : String(value);
        option.selected = (values.daily_limit_minutes ?? null) === value;
        limit.append(option);
      }
      limitLabel.append(limit);
      const quietLabel = node("label", "sv-inline");
      const quiet = node("input");
      quiet.type = "checkbox";
      quiet.checked = Boolean(values.quiet_hours);
      quietLabel.append(quiet, node("span", "", "Quiet hours (notifications wait until morning)"));
      const start = node("input");
      start.type = "time";
      start.value = minutesToTime(values.quiet_hours?.start_minute ?? 22 * 60);
      start.setAttribute("aria-label", "Quiet hours start");
      const end = node("input");
      end.type = "time";
      end.value = minutesToTime(values.quiet_hours?.end_minute ?? 7 * 60);
      end.setAttribute("aria-label", "Quiet hours end");
      const times = node("div", "sv-times");
      times.append(start, node("span", "", "to"), end);
      const sync = () => { times.hidden = !quiet.checked; };
      quiet.addEventListener("change", sync);
      sync();
      for (const control of [limit, quiet, start, end]) control.disabled = disabled;
      const save = node("button", "publish-button", "Save");
      save.type = "button";
      save.disabled = disabled;
      save.addEventListener("click", () => {
        const quietHours = quiet.checked
          ? { start_minute: timeToMinutes(start.value), end_minute: timeToMinutes(end.value) }
          : null;
        if (quietHours && (quietHours.start_minute === null || quietHours.end_minute === null
          || quietHours.start_minute === quietHours.end_minute)) {
          say("Quiet hours need a different start and end time.", true);
          return;
        }
        void onSave({ daily_limit_minutes: limit.value ? Number(limit.value) : null, quiet_hours: quietHours });
      });
      wrapper.append(limitLabel, quietLabel, times, save);
      return wrapper;
    }

    async function openTimeSettings() {
      if (!viewerId()) { hooks.signIn?.(); return; }
      const panel = sheet("Time and breaks");
      const settings = await refreshWellbeing();
      if (!settings) { panel.body.append(node("p", "form-error", "Your settings could not load.")); return; }
      panel.body.append(
        node("p", "sv-usage-today", `Today: ${minutesLabel(settings.today_minutes)}`),
        usageChart(settings.week || []),
        node("p", "sv-hint", "Time is counted while LOUDERR is open and in front on this and your other devices."),
      );
      if (settings.managed_by_guardian) {
        panel.body.append(node("p", "sv-hint",
          `${settings.guardian?.display_name || "Your guardian"} manages your daily limit and quiet hours.`));
      }
      panel.body.append(limitControls(settings, Boolean(settings.managed_by_guardian), async values => {
        try {
          state.wellbeing = await hooks.api("/api/account/wellbeing", { method: "PATCH", csrf: true, json: values });
          say("Saved");
        } catch (error) { say(error?.message || "Your settings were not saved.", true); }
      }));
      const breakLabel = node("label", "", "Break reminder");
      const breaks = node("select");
      for (const value of BREAK_CHOICES) {
        const option = node("option", "", value === null ? "Off" : `Every ${minutesLabel(value)}`);
        option.value = value === null ? "" : String(value);
        option.selected = (settings.break_reminder_minutes ?? null) === value;
        breaks.append(option);
      }
      breaks.addEventListener("change", async () => {
        try {
          state.wellbeing = await hooks.api("/api/account/wellbeing", {
            method: "PATCH", csrf: true, json: { break_reminder_minutes: breaks.value ? Number(breaks.value) : null },
          });
          state.activeSeconds = 0;
          state.breakShown = false;
          say("Saved");
        } catch (error) { say(error?.message || "Your settings were not saved.", true); }
      });
      breakLabel.append(breaks);
      const supervision = node("button", "sv-choice", "Supervision");
      supervision.type = "button";
      supervision.addEventListener("click", () => { panel.dismiss(); void openSupervision(); });
      panel.body.append(breakLabel, supervision);
    }

    // Supervision ----------------------------------------------------------------------------

    // An invitation opened while signed out waits in this tab, for an hour, until someone signs in.
    const INVITE_KEY = "louderr.supervision-invite";
    function keepInvite(token) {
      try { session?.setItem(INVITE_KEY, JSON.stringify({ token, at: Date.now() })); } catch (_) { /* optional */ }
    }
    function takeInvite() {
      try {
        const stored = JSON.parse(session?.getItem(INVITE_KEY) || "null");
        session?.removeItem(INVITE_KEY);
        const age = Date.now() - Number(stored?.at);
        return typeof stored?.token === "string" && age >= 0 && age < 3600000 ? stored.token : null;
      } catch (_) { return null; }
    }

    async function openSupervision({ inviteToken = null } = {}) {
      if (!viewerId()) { if (inviteToken) keepInvite(inviteToken); hooks.signIn?.(); return; }
      if (inviteToken) { await reviewInvite(inviteToken); return; }
      const panel = sheet("Supervision");
      let status;
      try { status = await hooks.api("/api/account/wellbeing/supervision"); }
      catch (error) { panel.body.append(node("p", "form-error", error?.message || "Supervision could not load.")); return; }
      const teen = status.as_teen;
      if (teen?.eligible) {
        panel.body.append(node("h3", "", "For you"));
        const link = teen.link;
        if (link?.status === "active") {
          panel.body.append(node("p", "", `${link.guardian?.display_name || "Your guardian"} can see how long you use LOUDERR and set your daily limit and quiet hours. They cannot see your messages, posts or who you follow.`));
          const end = node("button", "secondary-button sv-danger", "End supervision");
          end.type = "button";
          end.addEventListener("click", async () => {
            if (!(await hooks.confirm?.("End supervision?", { body: "Your guardian is told that it ended.", confirm: "End", destructive: true }))) return;
            try {
              await hooks.api(`/api/account/wellbeing/supervision/${encodeURIComponent(link.id)}`, { method: "DELETE", csrf: true });
              say("Supervision ended");
              panel.dismiss();
            } catch (error) { say(error?.message || "Supervision did not end.", true); }
          });
          panel.body.append(end);
        } else {
          panel.body.append(node("p", "", "Invite a parent or another adult you trust. They will see your daily minutes and can set a daily limit and quiet hours. They never see your messages, posts or who you follow, and you can end it at any time."));
          if (link?.status === "pending") panel.body.append(node("p", "sv-hint", "An invitation is waiting. Creating a new link replaces it."));
          const invite = node("button", "publish-button", "Create an invitation link");
          invite.type = "button";
          const result = node("div", "sv-invite");
          invite.addEventListener("click", async () => {
            try {
              const created = await hooks.api("/api/account/wellbeing/supervision/invite", { method: "POST", csrf: true });
              const url = new URL(created.url, location.origin).href;
              result.replaceChildren(node("p", "sv-hint", "Send this link to them. It works once and expires in 48 hours."));
              const field = node("input", "sv-field");
              field.readOnly = true;
              field.value = url;
              const copy = node("button", "secondary-button", "Copy link");
              copy.type = "button";
              copy.addEventListener("click", async () => {
                try { await navigator.clipboard.writeText(url); say("Link copied"); }
                catch (_) { field.select(); say("Select the link and copy it.", true); }
              });
              result.append(field, copy);
              if (navigator.share) {
                const share = node("button", "secondary-button", "Share");
                share.type = "button";
                share.addEventListener("click", () => navigator.share({ title: "LOUDERR supervision", url }).catch(() => {}));
                result.append(share);
              }
            } catch (error) { say(error?.message || "The link could not be created.", true); }
          });
          panel.body.append(invite, result);
        }
      }
      const guarded = status.as_guardian?.items || [];
      if (guarded.length) {
        panel.body.append(node("h3", "", "Teens you supervise"));
        for (const entry of guarded) {
          const card = node("article", "sv-supervised");
          card.append(node("strong", "", entry.teen.display_name || entry.teen.handle), usageChart(entry.week || []));
          card.append(limitControls(entry, false, async values => {
            try {
              await hooks.api(`/api/account/wellbeing/supervision/${encodeURIComponent(entry.link_id)}/limits`, {
                method: "PUT", csrf: true, json: values,
              });
              say(`Saved. ${entry.teen.display_name || entry.teen.handle} is told about the change.`);
            } catch (error) { say(error?.message || "The limits were not saved.", true); }
          }));
          const end = node("button", "secondary-button sv-danger", "End supervision");
          end.type = "button";
          end.addEventListener("click", async () => {
            if (!(await hooks.confirm?.("End supervision?", { confirm: "End", destructive: true }))) return;
            try {
              await hooks.api(`/api/account/wellbeing/supervision/${encodeURIComponent(entry.link_id)}`, { method: "DELETE", csrf: true });
              card.remove();
              say("Supervision ended");
            } catch (error) { say(error?.message || "Supervision did not end.", true); }
          });
          card.append(end);
          panel.body.append(card);
        }
      }
      if (!teen?.eligible && !guarded.length) {
        panel.body.append(node("p", "", "Supervision is for accounts aged 16 or 17. If you are a parent or guardian, ask your teen to send you an invitation link from their settings."));
      }
    }

    async function reviewInvite(token) {
      const panel = sheet("Supervision invitation");
      try {
        const preview = await hooks.api("/api/account/wellbeing/supervision/preview", {
          method: "POST", csrf: true, json: { token },
        });
        const list = (title, items) => {
          const block = node("div", "sv-invite-list");
          block.append(node("strong", "", title));
          const ul = node("ul");
          for (const item of items || []) ul.append(node("li", "", item));
          block.append(ul);
          return block;
        };
        panel.body.append(
          node("p", "", `${preview.teen.display_name || preview.teen.handle} (@${preview.teen.handle}) invited you to supervise their account.`),
          list("You will see", preview.you_will_see),
          list("You can set", preview.you_can_set),
          list("You will not see", preview.you_will_not_see),
        );
        const accept = node("button", "publish-button", "Accept");
        accept.type = "button";
        accept.addEventListener("click", async () => {
          try {
            await hooks.api("/api/account/wellbeing/supervision/accept", { method: "POST", csrf: true, json: { token } });
            say("Supervision started");
            panel.dismiss();
            void openSupervision();
          } catch (error) { say(error?.message || "The invitation could not be accepted.", true); }
        });
        const decline = node("button", "secondary-button", "Not now");
        decline.type = "button";
        decline.addEventListener("click", () => panel.dismiss());
        panel.body.append(accept, decline);
      } catch (error) {
        panel.body.append(node("p", "form-error", error?.message || "This invitation is not valid."));
      }
    }

    function syncIdentity() {
      syncRepostIdentity();
      const tab = state.repostTab;
      if (tab && !currentRepostTab(tab)) {
        const refresh = tab.grids.active?.() ? tab.grids.refresh : null;
        leaveProfileReposts();
        refresh?.();
      }
      if (state.wellbeingFor && state.wellbeingFor !== viewerId()) {
        state.wellbeing = null;
        state.wellbeingFor = null;
      }
      if (viewerId()) {
        if (!state.heartbeat) startTracking();
        void refreshWellbeing();
        const invite = takeInvite();
        if (invite) void reviewInvite(invite);
      } else stopTracking();
    }

    return {
      canRepost, toggleRepost, repostPending, makeRepostButton, refreshRepostButton, renderProfileTabs, loadReposts, profileRepost,
      profileRepostsReady, leaveProfileReposts, resetProfileReposts,
      openDisappearing, renderDisappearingBanner, syncThreadBanner, decorateMessage, expiryLabel,
      refreshWellbeing, openTimeSettings, openSupervision, syncIdentity, checkReminders,
    };
  }

  return {
    create,
    _test: { minutesLabel, expiryLabel, localDay, timeToMinutes, minutesToTime, reminderKey, TIMER_CHOICES, DAILY_LIMITS },
  };
});
